import { useAction, useMutation, usePaginatedQuery, useQuery } from "convex/react"
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react"
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { MentionSuggestions } from "@/components/app/MentionSuggestions"
import { PostCard } from "@/components/app/PostCard"
import { PostPageSkeleton } from "@/components/app/Skeletons"
import { Spinner } from "@/components/app/Spinner"
import { TickerSuggestions } from "@/components/app/TickerSuggestions"
import { useAppAuth } from "@/hooks/useAppAuth"
import { useMentionDetection } from "@/hooks/useMentionDetection"
import { useMentionPay } from "@/hooks/useMentionPay"
import { useSearchUsers } from "@/hooks/useSearchUsers"
import { useSolanaSign } from "@/hooks/useSolanaSign"
import { useWebBalance } from "@/hooks/useWebBalance"
import { isQuotaExhausted, isReplyLimitError } from "@/lib/errors"
import { api } from "~convex/_generated/api"
import type { Id } from "~convex/_generated/dataModel"
import type { EnrichedPost } from "~convex/queryHelpers"
import { MAX_POST_LENGTH, MICRO_USDC } from "~shared/constants"

type ReplySort = "recent" | "top"

interface ReplyComposeHandle {
	focus: () => void
}

const ReplyCompose = forwardRef<
	ReplyComposeHandle,
	{
		onSuccess: () => void
		parentAuthorHandle: string
		parentId: Id<"posts">
	}
>(function ReplyCompose({ onSuccess, parentAuthorHandle, parentId }, ref) {
	const { currentUser, isAuthenticated } = useAppAuth()
	const { signTransaction } = useSolanaSign()
	const { executeMentionPay } = useMentionPay()
	const { usdcBalance, refetchUSDC } = useWebBalance()
	const [content, setContent] = useState("")
	const [isSubmitting, setIsSubmitting] = useState(false)
	const textareaRef = useRef<HTMLTextAreaElement>(null)

	const mention = useMentionDetection(textareaRef, setContent)

	const mentionResults = useSearchUsers(
		mention.mentionQuery,
		mention.showMentionSuggestions && isAuthenticated,
		currentUser?._id ?? null,
	)
	const mentionItems = mentionResults?.slice(0, 5) ?? []

	const searchTokens = useAction(api.swapActions.searchTokens)
	const [tickerItems, setTickerItems] = useState<
		{
			mint: string
			ticker: string
			name: string
			icon: string | null
			market_cap: number | null
			price_change_24h: number | null
			is_verified: boolean
		}[]
	>([])
	const lastTickerQueryRef = useRef("")
	useEffect(() => {
		if (!mention.showTickerSuggestions || mention.tickerQuery.length < 2) {
			setTickerItems([])
			return
		}
		if (mention.tickerQuery === lastTickerQueryRef.current) {
			return
		}
		lastTickerQueryRef.current = mention.tickerQuery
		searchTokens({ query: mention.tickerQuery })
			.then(r => setTickerItems(r))
			.catch(() => setTickerItems([]))
	}, [mention.showTickerSuggestions, mention.tickerQuery, searchTokens])

	const freeCreateReply = useMutation(api.freeActions.freeCreateReply)
	const prepareReply = useAction(api.swapPrepare.prepareReply)
	const commitPost = useAction(api.swapPrepare.commitPost)

	const fees = useQuery(api.fees.getFees)
	const freeRepliesPerDay = fees?.freeRepliesPerDay ?? 20
	const isFreeReply = true // replies are always free-first (rate-limited server-side)
	const canAffordPaid = usdcBalance >= MICRO_USDC

	const charsRemaining = MAX_POST_LENGTH - content.length
	const canReply =
		content.trim().length > 0 &&
		charsRemaining >= 0 &&
		(isFreeReply || canAffordPaid) &&
		!isSubmitting

	useImperativeHandle(ref, () => ({
		focus: () => textareaRef.current?.focus(),
	}))

	useEffect(() => {
		textareaRef.current?.focus()
	}, [])

	const resizeTextarea = useCallback(() => {
		const textarea = textareaRef.current
		if (textarea) {
			textarea.style.height = "auto"
			textarea.style.height = `${textarea.scrollHeight}px`
		}
	}, [])

	const handleSubmit = async () => {
		if (!canReply) {
			return
		}
		setIsSubmitting(true)

		try {
			const trimmedContent = content.trim()
			let replyPostId: Id<"posts"> | undefined
			let usedFreeReply = false
			try {
				replyPostId = await freeCreateReply({
					content: trimmedContent,
					parent_id: parentId,
				})
				usedFreeReply = true
			} catch (e: unknown) {
				// Only fall through to paid for rate limit exhaustion
				if (!isQuotaExhausted(e) && !isReplyLimitError(e)) {
					throw e
				}
			}

			if (!usedFreeReply) {
				if (!canAffordPaid) {
					toast.error("Free replies used up and insufficient USDC balance")
					setIsSubmitting(false)
					return
				}
				const prepared = await prepareReply({
					parent_id: parentId,
					content: trimmedContent,
				})
				const signedSwapTx = await signTransaction(prepared.swapTx)
				const signedFeeTx = prepared.feeTx ? await signTransaction(prepared.feeTx) : undefined
				replyPostId = (await commitPost({
					content: trimmedContent,
					type: "reply",
					parent_id: parentId,
					prepare_token: prepared.prepareToken,
					signed_swap_tx: signedSwapTx,
					signed_fee_tx: signedFeeTx,
				})) as Id<"posts"> | undefined
				refetchUSDC()
			}

			if (replyPostId) {
				executeMentionPay(trimmedContent, replyPostId).catch(() => {})
			}
			toast.success("Reply posted!")
			setContent("")
			onSuccess()
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to post reply")
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<div className="border-app-border border-b px-3 py-3 sm:px-4">
			<div className="relative">
				<textarea
					className="min-h-[60px] w-full resize-none bg-transparent text-[15px] text-app-text leading-relaxed placeholder-app-text-tertiary outline-none"
					maxLength={MAX_POST_LENGTH + 50}
					onChange={e => {
						setContent(e.target.value)
						resizeTextarea()
						mention.detectMention()
					}}
					onClick={mention.detectMention}
					onKeyDown={e => mention.handleKeyDown(e, mentionItems, tickerItems)}
					placeholder={`Reply to @${parentAuthorHandle}...`}
					ref={textareaRef}
					rows={1}
					value={content}
				/>
				<MentionSuggestions
					items={mentionItems}
					onSelect={mention.insertMention}
					selectedIndex={mention.selectedIndex}
				/>
				{mention.showTickerSuggestions && (
					<TickerSuggestions
						items={tickerItems}
						onSelect={mention.insertTicker}
						selectedIndex={mention.selectedTickerIndex}
					/>
				)}
			</div>
			<div className="mt-2 flex items-center justify-between">
				<div className="flex items-center gap-2">
					<span className="text-app-green text-xs">Free replies ({freeRepliesPerDay}/day)</span>
					<span
						className={`text-xs ${
							charsRemaining < 0
								? "text-app-red"
								: charsRemaining < 20
									? "text-app-yellow"
									: "text-app-text-tertiary"
						}`}
					>
						{charsRemaining}
					</span>
				</div>
				<button
					className="rounded-full bg-white px-4 py-1 font-bold text-black text-sm transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
					disabled={!canReply}
					onClick={() => {
						handleSubmit().catch(() => {})
					}}
					type="button"
				>
					{isSubmitting ? <Spinner size="sm" /> : "Reply"}
				</button>
			</div>
		</div>
	)
})

export default function PostPage() {
	const { id } = useParams<{ id: string }>()
	const location = useLocation()
	const [searchParams] = useSearchParams()
	const postId = id ? (id as Id<"posts">) : null
	const [replySort, setReplySort] = useState<ReplySort>("recent")
	const { isAuthenticated } = useAppAuth()
	const [showReply, setShowReply] = useState(searchParams.get("reply") === "true")
	const replyRef = useRef<ReplyComposeHandle>(null)

	// Use post data passed via navigation state for instant render,
	// then let the Convex subscription take over for real-time updates
	const navigatedPost = (location.state as { post?: EnrichedPost } | null)?.post ?? null
	const queriedPost = useQuery(
		api.posts.getPost,
		isAuthenticated && postId ? { post_id: postId } : "skip",
	)
	// Use navigated post for instant render while query is loading.
	// Once query resolves, always trust it (handles deleted posts).
	const post = queriedPost ?? navigatedPost

	const parentChain = useQuery(
		api.posts.getPostParentChain,
		isAuthenticated && postId ? { post_id: postId } : "skip",
	)
	const replies = usePaginatedQuery(
		api.posts.getPostReplies,
		isAuthenticated && postId ? { post_id: postId } : "skip",
		{
			initialNumItems: 20,
		},
	)

	const sortedReplies = useMemo(() => {
		if (replySort === "top") {
			return [...replies.results].sort((a, b) => b.engagement_score - a.engagement_score)
		}
		return replies.results
	}, [replies.results, replySort])

	if (!postId) {
		return (
			<div className="py-12 text-center">
				<p className="text-app-text-secondary">Post not found</p>
				<Link className="mt-2 inline-block text-neon text-sm hover:underline" to="/">
					Back to feed
				</Link>
			</div>
		)
	}

	// Query still loading and no navigated post to render yet
	if (!post && queriedPost === undefined) {
		return <PostPageSkeleton />
	}

	// Query resolved: post doesn't exist (takes precedence over stale navigated post)
	if (queriedPost === null) {
		return (
			<div className="py-12 text-center">
				<p className="text-app-text-secondary">Post not found</p>
				<Link className="mt-2 inline-block text-neon text-sm hover:underline" to="/">
					Back to feed
				</Link>
			</div>
		)
	}

	if (!post) {
		return <PostPageSkeleton />
	}

	return (
		<div className="animate-fade-in">
			{parentChain && parentChain.length > 0 && (
				<div className="relative">
					{parentChain.map((parent, index) => (
						<div className="relative border-app-border border-b" key={parent._id}>
							<div
								className="absolute top-0 bottom-0 left-[36px] w-0.5 bg-app-border-light"
								style={{
									top: index === 0 ? "50%" : 0,
								}}
							/>
							<PostCard post={parent} />
						</div>
					))}
					<div
						className="absolute bottom-0 left-[36px] w-0.5 bg-app-border-light"
						style={{ height: 0 }}
					/>
				</div>
			)}

			<div className="relative border-app-border border-b">
				{parentChain && parentChain.length > 0 && (
					<div
						className="absolute top-0 left-[36px] w-0.5 bg-app-border-light"
						style={{ height: "28px" }}
					/>
				)}
				<PostCard
					onReply={() => {
						setShowReply(true)
						// Focus textarea if compose is already open
						replyRef.current?.focus()
					}}
					post={post}
				/>
			</div>

			{isAuthenticated && showReply && (
				<ReplyCompose
					onSuccess={() => setShowReply(false)}
					parentAuthorHandle={post.author?.handle ?? "unknown"}
					parentId={postId}
					ref={replyRef}
				/>
			)}

			{isAuthenticated && !showReply && (
				<button
					className="w-full border-app-border border-b px-3 py-3 text-left text-app-text-tertiary text-sm transition-colors hover:bg-app-surface sm:px-4"
					onClick={() => setShowReply(true)}
					type="button"
				>
					Reply to @{post.author?.handle ?? "unknown"}...
				</button>
			)}

			<div className="border-app-border border-b px-3 py-2 sm:px-4">
				<div className="flex items-center justify-between">
					<span className="font-semibold text-app-text-secondary text-sm">Replies</span>
					<div className="flex gap-1">
						{(["recent", "top"] as const).map(tab => (
							<button
								className={`px-2.5 py-1 font-medium text-xs transition-colors ${
									replySort === tab
										? "border-neon border-b-2 text-app-text"
										: "text-app-text-secondary hover:text-app-text"
								}`}
								key={tab}
								onClick={() => setReplySort(tab)}
								type="button"
							>
								{tab === "recent" ? "Recent" : "Top"}
							</button>
						))}
					</div>
				</div>
			</div>

			{sortedReplies.map(reply => (
				<PostCard key={reply._id} post={reply} />
			))}

			{replies.status === "LoadingMore" && (
				<div className="flex justify-center py-4">
					<Spinner size="sm" />
				</div>
			)}

			{replies.status === "CanLoadMore" && (
				<button
					className="w-full py-3 text-center text-neon text-sm transition-opacity hover:opacity-80"
					onClick={() => replies.loadMore(20)}
					type="button"
				>
					Load more replies
				</button>
			)}

			{replies.results.length === 0 && replies.status === "Exhausted" && (
				<div className="py-8 text-center text-app-text-secondary text-sm">No replies yet</div>
			)}
		</div>
	)
}
